import { ReactNode, useRef, useState } from 'react';
import { A11y, Keyboard, Mousewheel, Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import { Box, Flex, IconButton, Text } from '@chakra-ui/react';

import { ChevronLeftIcon, ChevronRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useI18n } from '~/hooks/use-i18n';

import { getCarouselEdgeState } from '../utils/get-carousel-edge-state';

import 'swiper/css';
import 'swiper/css/mousewheel';

/** Swiper 9 runtime API; omitted from its published type definitions. */
type SwiperWithTranslateBounds = SwiperClass & {
  maxTranslate(): number;
  minTranslate(): number;
};

export type CardCarouselSlide = {
  key: string;
  content: ReactNode;
};

export type CardCarouselProps = {
  /** Accessible name for the scroll region and its arrows, e.g. `graduate testimonials`. */
  label: string;
  /** Subsection heading, pinned to the start of the header row. */
  title: ReactNode;
  /** Count or rating summary, pinned to the end of the header row beside the arrows. */
  meta?: ReactNode;
  slides: CardCarouselSlide[];
  /** Gap between cards, in pixels. Doubles as the Swiper slide spacing. */
  spaceBetween?: number;
  onArrowClick?: () => void;
};

const ARROW_SIZE = '36px';

/**
 * `ContentContainer`'s gutter, which the track cancels out so cards can scroll
 * to the edge of the viewport instead of stopping short of it. The slide
 * offsets put the gutter back in front of the first card and behind the last,
 * keeping them flush with the heading above. Values must mirror the container's
 * `mobilePadding` / `mdPadding`; from `lg` up it manages its own centering.
 */
const GUTTER_PX = { base: 16, md: 24, lg: 0 };
const TRACK_BLEED = { base: `-${GUTTER_PX.base}px`, md: `-${GUTTER_PX.md}px`, lg: '0px' };
const TRACK_OFFSETS = {
  0: { slidesOffsetBefore: GUTTER_PX.base, slidesOffsetAfter: GUTTER_PX.base },
  768: { slidesOffsetBefore: GUTTER_PX.md, slidesOffsetAfter: GUTTER_PX.md },
  992: { slidesOffsetBefore: GUTTER_PX.lg, slidesOffsetAfter: GUTTER_PX.lg },
};

/** Figma `Heading/3XS/Semibold`, which has no size token in the v2 theme. */
export function CarouselTitle({ ariaLabel, children }: { ariaLabel?: string; children: ReactNode }) {
  return (
    <Text
      aria-label={ariaLabel}
      as='h3'
      color='supportGray.100'
      fontSize='20px'
      fontWeight='600'
      letterSpacing='-0.6px'
      lineHeight='24px'
    >
      {children}
    </Text>
  );
}

export function CardCarousel({ label, title, meta, slides, spaceBetween = 16, onArrowClick }: CardCarouselProps) {
  const { i18n, isRtl } = useI18n();
  const swiperRef = useRef<SwiperRef>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(true);

  function syncEdges(swiper: SwiperClass) {
    const edgeState = getCarouselEdgeState(swiper as SwiperWithTranslateBounds);
    setIsBeginning(edgeState.isBeginning);
    setIsEnd(edgeState.isEnd);
  }

  function scrollTo(edge: 'start' | 'end') {
    onArrowClick?.();
    const swiper = swiperRef.current?.swiper;
    if (edge === 'end') swiper?.slideNext();
    else swiper?.slidePrev();
  }

  return (
    <Flex flexDirection='column' gap={2} width='100%'>
      {/* Below `md` the title takes its own row: sharing one row lets the flex
          item collapse far enough to break the heading mid-word. */}
      <Flex
        alignItems={{ base: 'flex-start', md: 'flex-end' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 2, md: 4 }}
      >
        <Box flex={{ md: '1 0 0' }} minWidth={0} width={{ base: '100%', md: 'auto' }}>
          {title}
        </Box>
        <Flex
          alignItems='center'
          gap={4}
          justifyContent='space-between'
          minWidth={0}
          width={{ base: '100%', md: 'auto' }}
        >
          {meta}
          <Flex flexShrink={0} gap={2} justifyContent='flex-end'>
            <IconButton
              aria-label={`Show previous ${label}`}
              borderRadius='full'
              boxSize={ARROW_SIZE}
              isDisabled={isBeginning}
              minWidth={ARROW_SIZE}
              variant='outline'
              icon={
                isRtl ? <ChevronRightIcon height='16px' width='16px' /> : <ChevronLeftIcon height='16px' width='16px' />
              }
              onClick={() => scrollTo('start')}
            />
            <IconButton
              aria-label={`Show next ${label}`}
              borderRadius='full'
              boxSize={ARROW_SIZE}
              isDisabled={isEnd}
              minWidth={ARROW_SIZE}
              variant='outline'
              icon={
                isRtl ? <ChevronLeftIcon height='16px' width='16px' /> : <ChevronRightIcon height='16px' width='16px' />
              }
              onClick={() => scrollTo('end')}
            />
          </Flex>
        </Flex>
      </Flex>
      <Box
        isolation='isolate'
        marginX={TRACK_BLEED}
        sx={{
          '.swiper-slide': {
            display: 'flex',
            height: 'auto',
            width: 'auto',
          },
        }}
      >
        <Swiper
          ref={swiperRef}
          a11y={{ containerMessage: label }}
          breakpoints={TRACK_OFFSETS}
          keyboard={{ enabled: true, onlyInViewport: true }}
          modules={[A11y, Keyboard, Mousewheel]}
          mousewheel={{ forceToAxis: true, thresholdDelta: 10 }}
          slidesPerView='auto'
          spaceBetween={spaceBetween}
          onResize={syncEdges}
          onSlideChange={syncEdges}
          onSwiper={syncEdges}
          onTransitionEnd={syncEdges}
          onUpdate={syncEdges}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.key}>{slide.content}</SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Flex>
  );
}
